import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Field,
    Input,
    Checkbox,
    ColorArea,
    ColorPicker,
    ColorSlider,
    MessageBar,
    TableCellLayout,
    TableColumnSizingOptions,
    Tooltip,
    ToolbarButton,
    createTableColumn,
    TableColumnDefinition,
} from '@fluentui/react-components';
import { Add24Regular, Checkmark24Regular, Delete24Regular, Edit24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import ConfigurationTable from '../../components/ConfigurationTable/ConfigurationTable';
import { IConfigRow } from '../../components/ConfigurationTable/ConfigurationTable.utils';
import { useAppDispatch, useSelector } from '../../../../store/hooks';
import {
    loadConversationStatuses,
    removeConversationStatus,
    selectConversationStatuses,
    upsertConversationStatus,
} from '../../../../store/features/conversationStatuses/conversationStatusesSlice';
import { ConversationStatusEntity } from '../../../../model/ConversationStatusEntity';
import ConfirmDeletionDialog from '../../components/ConfirmDeletionDialog/ConfirmDeletionDialog';
import { useConfirmDeletionDialog } from '../../components/ConfirmDeletionDialog/useConfirmDeletionDialog';
import { useConversationStatusesPanelStyles } from './ConversationStatusesPanel.styles';

interface StatusRow extends IConfigRow { status: ConversationStatusEntity; }
type HsvColor = { h: number; s: number; v: number; a?: number };
const MAX_PRIORITY = 2147483647;
const MAX_TITLE_LENGTH = 128;
const hasInvalidRowKeyCharacters = (value: string): boolean =>
    value.includes('/') || value.includes('\\') || value.includes('#') || value.includes('?') ||
    Array.from(value).some((character) => character.charCodeAt(0) < 0x20);

const getTitleError = (
    title: string,
    editing: ConversationStatusEntity | null,
    items: ConversationStatusEntity[],
    t: TFunction
): string | undefined => {
    const trimmedTitle = title.trim();
    if (trimmedTitle === '') {
        return t('ConversationStatus_Name_Required', 'A status name is required.');
    }
    if (trimmedTitle.length > MAX_TITLE_LENGTH) {
        return t('ConversationStatus_Name_Max', 'The status name cannot exceed 128 characters.');
    }
    if (!editing?.rowKey && hasInvalidRowKeyCharacters(trimmedTitle)) {
        return t('ConversationStatus_Name_InvalidCharacters', 'The status name contains invalid characters.');
    }
    if (items.some((status) =>
        status.rowKey !== editing?.rowKey &&
        status.title.trim().toLowerCase() === trimmedTitle.toLowerCase())) {
        return t('ConversationStatus_Name_Duplicate', 'Each status must have a unique name.');
    }
    return undefined;
};

const getPriorityError = (
    priority: string,
    editing: ConversationStatusEntity | null,
    items: ConversationStatusEntity[],
    t: TFunction
): string | undefined => {
    const priorityNumber = Number(priority);
    if (priority.trim() === '' || !Number.isInteger(priorityNumber) || priorityNumber < 0) {
        return t('ConversationStatus_Priority_Invalid', 'Priority must be a non-negative whole number.');
    }
    if (priorityNumber > MAX_PRIORITY) {
        return t('ConversationStatus_Priority_Max', 'Priority cannot be greater than 2147483647.');
    }
    if (items.some((status) => status.statusPriority === priorityNumber && status.rowKey !== editing?.rowKey)) {
        return t('ConversationStatus_Priority_Duplicate', 'Each status must have a unique priority.');
    }
    return undefined;
};

const hexToHsv = (hexValue: string): HsvColor => {
    const hexNumber = parseInt(hexValue.replace('#', ''), 16);
    const red = ((hexNumber >> 16) & 255) / 255;
    const green = ((hexNumber >> 8) & 255) / 255;
    const blue = (hexNumber & 255) / 255;
    const max = Math.max(red, green, blue);
    const min = Math.min(red, green, blue);
    const delta = max - min;
    let hue = 0;
    if (delta) {
        hue = max === red ? 60 * (((green - blue) / delta) % 6) : max === green ? 60 * ((blue - red) / delta + 2) : 60 * ((red - green) / delta + 4);
    }
    return { h: hue < 0 ? hue + 360 : hue, s: max ? delta / max : 0, v: max, a: 1 };
};

const getNextPriority = (items: ConversationStatusEntity[]): number => {
    const usedPriorities = new Set(items.map((item) => item.statusPriority));
    let nextPriority = 0;
    while (usedPriorities.has(nextPriority) && nextPriority < MAX_PRIORITY) {
        nextPriority++;
    }
    return usedPriorities.has(nextPriority) ? 0 : nextPriority;
};

const hsvToHex = ({ h, s, v }: HsvColor): string => {
    const chroma = v * s;
    const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
    const match = v - chroma;
    const rgb = h < 60 ? [chroma, x, 0] : h < 120 ? [x, chroma, 0] : h < 180 ? [0, chroma, x] : h < 240 ? [0, x, chroma] : h < 300 ? [x, 0, chroma] : [chroma, 0, x];
    return rgb.map((channel) => Math.round((channel + match) * 255).toString(16).padStart(2, '0')).join('').toUpperCase();
};

const ConversationStatusesPanel: React.FC = () => {
    const { t } = useTranslation();
    const styles = useConversationStatusesPanelStyles();
    const dispatch = useAppDispatch();
    const { items, loading } = useSelector(selectConversationStatuses);
    const search = useSelector((x) => x.appConfiguration.searchQuery).toLowerCase();
    const [editing, setEditing] = useState<ConversationStatusEntity | null>(null);
    const [title, setTitle] = useState('');
    const [colour, setColour] = useState('#000000');
    const [priority, setPriority] = useState('0');
    const [pauseHandlingTime, setPauseHandlingTime] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [operationError, setOperationError] = useState<string | undefined>();

    const [color, setColor] = useState<HsvColor>({ h: 0, s: 1, v: 1, a: 1 });

    const titleError = getTitleError(title, editing, items, t);
    const colourError = !/^#[0-9a-f]{6}$/i.test(colour)
        ? t('ConversationStatus_Colour_Invalid', 'Enter a six-digit hexadecimal colour.')
        : undefined;
    const priorityNumber = Number(priority);
    const priorityError = getPriorityError(priority, editing, items, t);

    useEffect(() => { dispatch(loadConversationStatuses()); }, [dispatch]);

    const {
        isOpen: deletionDialogOpen,
        itemId: deletionItemId,
        message: deletionDialogMessage,
        openDialog: openDeletionDialog,
        closeDialog: closeDeletionDialog,
    } = useConfirmDeletionDialog(
        useCallback((itemName: string) => t('ConversationStatus_DeleteConfirm_Name', 'Delete conversation status "{name}"?').replace('{name}', itemName), [t])
    );

    const openEditor = useCallback((status?: ConversationStatusEntity) => {
        const defaultPriority = getNextPriority(items);
        setOperationError(undefined);
        setEditing(status ?? { rowKey: '', title: '', colour: '000000', statusPriority: defaultPriority, isDefault: false, isInProgress: false, isAwaitingResponse: false, pauseHandlingTime: false, isAutoGenerated: false, isResolved: false, isRequired: false, canDelete: true });
        setTitle(status?.title ?? '');
        setColour(`#${status?.colour ?? '000000'}`);
        setColor(hexToHsv(status?.colour ?? '000000'));
        setPriority(String(status?.statusPriority ?? defaultPriority));
        setPauseHandlingTime(status?.pauseHandlingTime ?? false);
    }, [items]);

    const handleDelete = useCallback((rowKey: string, itemName: string) => {
        setOperationError(undefined);
        openDeletionDialog({ itemId: rowKey, itemName });
    }, [openDeletionDialog]);

    const handleConfirmDelete = useCallback(async () => {
        if (!deletionItemId || deleting) return;

        setDeleting(true);
        setOperationError(undefined);
        try {
            await dispatch(removeConversationStatus(deletionItemId)).unwrap();
            closeDeletionDialog();
        } catch (error) {
            setOperationError(error instanceof Error ? error.message : t('ConversationStatus_DeleteFailed', 'The conversation status could not be deleted.'));
        } finally {
            setDeleting(false);
        }
    }, [closeDeletionDialog, deletionItemId, deleting, dispatch, t]);

    const handleSave = useCallback(async () => {
        if (!editing || titleError || colourError || priorityError || saving) return;

        setSaving(true);
        setOperationError(undefined);
        try {
            await dispatch(upsertConversationStatus({
                rowKey: editing.rowKey,
                title: title.trim(),
                colour: colour.slice(1),
                statusPriority: priorityNumber,
                isDefault: editing.isDefault,
                isInProgress: editing.isInProgress,
                isAwaitingResponse: editing.isAwaitingResponse,
                pauseHandlingTime: editing.isRequired ? editing.pauseHandlingTime : pauseHandlingTime,
                isAutoGenerated: editing.isAutoGenerated,
            })).unwrap();
            setEditing(null);
        } catch (error) {
            setOperationError(error instanceof Error ? error.message : t('ConversationStatus_SaveFailed', 'The conversation status could not be saved.'));
        } finally {
            setSaving(false);
        }
    }, [colour, colourError, dispatch, editing, pauseHandlingTime, priorityError, priorityNumber, saving, t, title, titleError]);

    const rows = useMemo<StatusRow[]>(() => items
        .filter((x) => x.title.toLowerCase().includes(search))
        .map((status) => ({ id: status.rowKey, value: status.title, status })), [items, search]);

    const columnSizingOptions = useMemo<TableColumnSizingOptions>(() => ({
        value: {
            minWidth: 120,
            idealWidth: 220
        },
        colour: {
            minWidth: 100,
            idealWidth: 130
        },
        priority: {
            minWidth: 80,
            idealWidth: 90
        },
        pauseHandlingTime: {
            minWidth: 140,
            idealWidth: 170
        },
        required: {
            minWidth: 70,
            idealWidth: 80
        },
        actions: {
            minWidth: 100,
            idealWidth: 100
        },
    }), []);

    const columns = useMemo<TableColumnDefinition<StatusRow>[]>(() => [
        createTableColumn<StatusRow>({
            columnId: 'value',
            compare: (a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: 'base' }),
            renderHeaderCell: () => t('TableHeader_ConversationStatus'),
            renderCell: (row) => row.value
        }),
        createTableColumn<StatusRow>({
            columnId: 'colour',
            compare: (a, b) => a.status.colour.localeCompare(b.status.colour, undefined, { sensitivity: 'base' }),
            renderHeaderCell: () => t('ConversationStatus_Colour'),
            renderCell: (row) => (
                <TableCellLayout
                    media={
                        <span
                            role="img"
                            title={`#${row.status.colour}`}
                            aria-label={t('ConversationStatus_ColourValue', {
                                colour: row.status.colour,
                            })}
                            data-colour={`#${row.status.colour}`}
                            className={styles.colourSwatch}
                        />
                    }
                >
                    #{row.status.colour}
                </TableCellLayout>
            )
        }),
        createTableColumn<StatusRow>({
            columnId: 'priority',
            compare: (a, b) => a.status.statusPriority - b.status.statusPriority,
            renderHeaderCell: () => t('ConversationStatus_Priority'),
            renderCell: (row) => row.status.statusPriority
        }),
        createTableColumn<StatusRow>({
            columnId: 'pauseHandlingTime',
            compare: (a, b) => Number(a.status.pauseHandlingTime) - Number(b.status.pauseHandlingTime),
            renderHeaderCell: () => t('ConversationStatus_PauseHandlingTime_Column'),
            renderCell: (row) => row.status.pauseHandlingTime ? t('Yes') : t('No')
        }),
        createTableColumn<StatusRow>({
            columnId: 'required',
            compare: (a, b) => Number(a.status.isRequired) - Number(b.status.isRequired),
            renderHeaderCell: () => t('ConversationStatus_Required'),
            renderCell: (row) => {
                const reasons = [
                    row.status.isDefault && t('ConversationStatus_IsDefault'),
                    row.status.isInProgress && t('ConversationStatus_IsInProgress'),
                    row.status.isAwaitingResponse && t('ConversationStatus_IsAwaitingResponse'),
                    row.status.isAutoGenerated && t('ConversationStatus_IsAutoGenerated'),
                    row.status.isResolved && t('ConversationStatus_IsResolved'),
                ].filter((reason): reason is string => Boolean(reason));
                const requiredDescription = `${t('ConversationStatus_RequiredFor')}: ${reasons.length > 0 ? reasons.join(', ') : t('ConversationStatus_Required')}`;

                return <TableCellLayout>{row.status.isRequired && <Tooltip content={requiredDescription} relationship="label"><span aria-label={requiredDescription}><Checkmark24Regular /></span></Tooltip>}</TableCellLayout>;
            }
        }),
        createTableColumn<StatusRow>({
            columnId: 'actions',
            renderHeaderCell: () => null,
            renderCell: (row) =>
                <TableCellLayout>
                    <Button appearance="transparent"
                        icon={<Edit24Regular />}
                        aria-label={t('Tooltip_Edit')}
                        onClick={() => openEditor(row.status)} />
                    {row.status.canDelete &&
                        <Button
                            appearance="transparent"
                            icon={<Delete24Regular />}
                            aria-label={t('Tooltip_Delete')}
                            onClick={() => handleDelete(row.id, row.value)}
                        />}
                </TableCellLayout>
        }),
    ], [handleDelete, openEditor, styles.colourSwatch, t]);

    const handleChange = (_: unknown, data: { color: HsvColor }) => {
        const nextColor = { ...data.color, a: data.color.a ?? 1 };
        setColor(nextColor);
        setColour(`#${hsvToHex(nextColor)}`);
    };

    return <>
        {operationError && <MessageBar intent="error">{operationError}</MessageBar>}
        <ConfigurationTable
            items={rows}
            columns={columns}
            columnSizingOptions={columnSizingOptions}
            ariaLabel={t('ConversationStatus_TableLabel')}
            loadStatus={loading}
            bottomActions={<ToolbarButton icon={<Add24Regular />} onClick={() => openEditor()}>{t('ConversationStatus_Add')}</ToolbarButton>} />
        <Dialog
            open={!!editing}
            onOpenChange={(_, data) => { if (!data.open) setEditing(null); }}
        >
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>
                        {t(editing?.rowKey ? 'ConversationStatus_EditTitle' : 'ConversationStatus_AddTitle')}
                    </DialogTitle>
                    <DialogContent>
                        <Field
                            label={t('ConversationStatus_Name')}
                            required
                            validationState={titleError ? 'error' : 'none'}
                            validationMessage={titleError}
                        >
                            <Input
                                value={title}
                                onChange={(_, data) => setTitle(data.value)}
                            />
                        </Field>
                        <Field
                            label={t('ConversationStatus_Colour')}
                            required
                            validationState={colourError ? 'error' : 'none'}
                            validationMessage={colourError}
                        >
                            <ColorPicker
                                color={color}
                                onColorChange={handleChange}
                            >
                                <ColorArea
                                    inputX={{ 'aria-label': t('ConversationStatus_Saturation') }}
                                    inputY={{ 'aria-label': t('ConversationStatus_Brightness') }}
                                />
                                <ColorSlider aria-label={t('ConversationStatus_Hue')} />
                            </ColorPicker>
                            <Input
                                aria-label={t('ConversationStatus_HexColour')}
                                value={colour}
                                placeholder="#RRGGBB"
                                onChange={(_, data) => {
                                    setColour(data.value);
                                    if (/^#[0-9a-f]{6}$/i.test(data.value)) {
                                        setColor(hexToHsv(data.value));
                                    }
                                }}
                            />
                        </Field>
                        <Field
                            label={t('ConversationStatus_Priority')}
                            required
                            validationState={priorityError ? 'error' : 'none'}
                            validationMessage={priorityError}
                        >
                            <Input
                                type="number"
                                min={0}
                                max={MAX_PRIORITY}
                                value={priority}
                                onChange={(_, data) => setPriority(data.value)}
                                aria-invalid={!!priorityError}
                            />
                        </Field>
                        {!editing?.isRequired && <Checkbox
                            label={t('ConversationStatus_PauseHandlingTime')}
                            checked={pauseHandlingTime}
                            onChange={(_, data) => setPauseHandlingTime(!!data.checked)}
                        />}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => setEditing(null)}
                        >
                            {t('Dialog_Common_Cancel')}
                        </Button>
                        <Button
                            appearance="primary"
                            disabled={!!titleError || !!colourError || !!priorityError || saving}
                            onClick={handleSave}
                        >
                            {saving ? t('ConversationStatus_Saving', 'Saving...') : t('Button_Common_Save')}
                        </Button>
                    </DialogActions></DialogBody></DialogSurface>
        </Dialog>
        <ConfirmDeletionDialog
            open={deletionDialogOpen}
            message={deletionDialogMessage}
            onConfirm={handleConfirmDelete}
            onClose={closeDeletionDialog}
        />
    </>;
};

export default ConversationStatusesPanel;